import { MDText } from 'i18n-react';

import { stripStringAndCamelCase } from 'Utilities';
import routing, {
  AFFILIATE_PAGE_ROUTES,
  BRANDS_SECTION_ROUTES,
  CAR_PRIZE_DRAW_ROUTE,
  DRIVING_GLOVES_PRIZE_DRAW_ROUTE,
  EARLY_DOCS_COLLECTION,
  EARLY_DOCS_VERIFICATION,
  FINANCE_PAYMENT_DETAILS_ROUTE,
  FINANCE_PAYMENT_DETAILS_ROUTE_MORE_FUNDS_REQUESTED,
  HANDSHAKE_ROUTE,
  POST_SALE_DOCS_COLLECTION,
  SERVICE_FEES_ROUTE,
  SITEMAP_HTML_URL,
  VEHICLE_COLLECTION_ROUTE,
  VERIFY_YOUR_IDENTITY_ROUTE,
} from 'Utilities/routing';
import { CAREERS_URL, CONTACT_URL, HELP_URL, PRO_URL } from 'Utilities/urls';
import {
  FULL_LANDING_PAGE_ROUTES,
  GUIDES_PATH,
  MOT_PAGE_ROUTES,
  MOT_VALUATION_ROUTE,
  NO_SITEMAP_ROUTES,
  TAX_PAGE_ROUTES,
  TAX_VALUATION_ROUTE,
  WORDPRESS_ROUTES_CONFIG,
} from 'Utilities/wordpress';

import { PAAS_ROUTE } from 'PaaS/paas.constants';

import { ULEZ_URL_PATH } from './containers/app/contentPages/ulezPage/UlezPage.helpers';
import { CUSTOMER_HUB_PATHS, CUSTOMER_HUB_RESULT_ROUTE, CUSTOMER_HUB_VALUATION_ROUTE } from './containers/app/customerHub/CustomerHubHelpers';
import { SEO_PAGES_ROUTES } from './containers/app/SeoPages/helpers';
import { INELIGIBLE_URL } from './containers/app/valuations/valuations.constants';
import Texts from './texts.json';

const T = new MDText(Texts);

const ulezCheckerFooterRoutes = Object.freeze([
  ULEZ_URL_PATH,
]);

const routes = [
  {
    cache: true,
    hideReviews: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: '/',
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    cache: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('referView.title'),
    meta: {
      description: T.translate('referView.metaDescription'),
      title: T.translate('referView.metaTitle'),
    },
    path: '/refer',
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    cache: true,
    hideReviews: false,
    label: T.translate('components.navigation.howItWorksLabel'),
    meta: {
      description: T.translate('howItWorksView.metaDescription'),
      title: T.translate('howItWorksView.metaTitle'),
    },
    nav: true,
    path: '/how-it-works',
    showDisclaimer: true,
    showDisclaimerDetail: true,
    zendeskWidget: true,
  },
  {
    cache: true,
    hideReviews: true,
    label: T.translate('customerReviewsView.title'),
    meta: {
      description: T.translate('customerReviewsView.metaDescription'),
      title: T.translate('customerReviewsView.metaTitle'),
    },
    path: '/reviews',
    zendeskWidget: true,
  },
  {
    cache: true,
    hideReviews: true,
    label: T.translate('helpView.title'),
    nav: true,
    path: HELP_URL,
  },
  {
    label: T.translate('components.navigation.toolsLabel'),
    name: 'tools',
    nav: true,
    subRoutes: [
      { label: T.translate('components.navigation.valueTrackerLabel'), path: CUSTOMER_HUB_VALUATION_ROUTE },
      { label: T.translate('components.navigation.motLabel'), path: MOT_VALUATION_ROUTE },
      { isNewRoute: true, label: T.translate('components.navigation.taxCheckLabel'), path: TAX_VALUATION_ROUTE },
      { label: T.translate('components.navigation.ulezLabel'), path: '/ulez-checker' },
    ],
  },
  {
    label: T.translate('components.navigation.moreLabel'),
    name: 'more',
    nav: true,
    subRoutes: [
      { label: T.translate('aboutView.title'), path: '/about' },
      { label: T.translate('whyMotorwayView.title'), path: '/why-motorway' },
      { label: T.translate('guidesView.title'), path: GUIDES_PATH },
      { label: T.translate('authorsAndExpertsView'), path: '/guides/authors' },
      { label: T.translate('jobsView.title'), path: CAREERS_URL },
      { label: T.translate('components.navigation.motorwayForDealersLabel'), path: PRO_URL },
      { label: T.translate('contactView.title'), path: CONTACT_URL },
    ],
  },
  {
    cache: true,
    hideReviews: false,
    label: T.translate('aboutView.title'),
    meta: { description: T.translate('aboutView.metaDescription'), title: T.translate('aboutView.metaTitle') },
    path: '/about',
    wordpress: false,
    zendeskWidget: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('vehiclePayment.title'),
    meta: { title: T.translate('vehiclePayment.metaTitle') },
    noRobots: true,
    path: `${routing.vrmPath}/vehicle-payment`,
    vrmPath: true,
  },
  {
    cache: true,
    hideReviews: true,
    label: T.translate('brandsBaseView.title'),
    meta: { description: T.translate('brandsBaseView.metaDescription'), title: T.translate('brandsBaseView.metaTitle') },
    path: BRANDS_SECTION_ROUTES[0],
    refreshMetaManually: true,
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideVehicleLookup: true,
    label: T.translate('signUpView.eligible.title'),
    meta: { description: T.translate('signUpView.metaDescription'), title: T.translate('signUpView.metaTitle') },
    noRobots: true,
    path: `${routing.vrmPath}/sign-up`,
    vrmPath: true,
  },
  {
    // Old profiling form, we can't delete this yet otherwise the redirect to `/{vrm}/profile` won't work
    noRobots: true,
    path: `${routing.vrmPath}/vehicle-details/:step?`,
    vrmPath: true,
  },
  {
    hideFooter: true,
    label: T.translate('detailsFormView.title'),
    manageOwnScroll: true,
    meta: { title: T.translate('detailsFormView.metaTitle') },
    noRobots: true,
    path: `${routing.vrmPath}/profile/:step?/:step?/:step?`,
    vrmPath: true,
    zendeskWidget: true,
  },
  {
    hideFooter: true,
    noRobots: true,
    path: PAAS_ROUTE,
    vrmPath: true,
    zendeskWidget: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('vehicleCollectionView.title'),
    manageOwnScroll: true,
    meta: { title: T.translate('vehicleCollectionView.metaTitle') },
    noRobots: true,
    path: `${routing.vrmPath}${VEHICLE_COLLECTION_ROUTE}`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('financePaymentDetailsRoute.title'),
    meta: {
      description: T.translate('financePaymentDetailsRoute.metaDescription'),
      title: T.translate('financePaymentDetailsRoute.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}${FINANCE_PAYMENT_DETAILS_ROUTE}`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('financePaymentDetailsRoute.title'),
    meta: {
      description: T.translate('financePaymentDetailsRoute.metaDescription'),
      title: T.translate('financePaymentDetailsRoute.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}${FINANCE_PAYMENT_DETAILS_ROUTE_MORE_FUNDS_REQUESTED}`,
    vrmPath: true,
  },
  {
    cache: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('signInView.metaTitle'),
    meta: { description: T.translate('signInView.metaDescription'), title: T.translate('signInView.metaTitle') },
    path: '/sign-in',
  },
  {
    hideVehicleLookup: true,
    label: T.translate('tokenExpiredView.title'),
    meta: { description: T.translate('tokenExpiredView.metaDescription'), title: T.translate('tokenExpiredView.metaTitle') },
    noRobots: true,
    path: '/token-expired',
  },
  {
    hideReviews: false,
    hideVehicleLookup: true,
    label: T.translate('vehicleView.title'),
    meta: { description: T.translate('vehicleView.metaDescription') },
    noRobots: true,
    path: `${routing.vrmPath}`,
    showDisclaimer: true,
    showDisclaimerDetail: true,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('accountView.title'),
    meta: { description: T.translate('accountView.metaDescription'), title: T.translate('accountView.metaTitle') },
    path: '/account',
    zendeskWidget: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('ineligibleView.title'),
    meta: { description: T.translate('ineligibleView.metaDescription'), title: T.translate('ineligibleView.metaTitle') },
    noRobots: true,
    path: `${routing.vrmPath}/${INELIGIBLE_URL}`,
    zendeskWidget: true,
  },
  {
    cache: true,
    label: T.translate('checkoutView.title'),
    meta: { title: T.translate('checkoutView.metaTitle') },
    noRobots: true,
    noState: true,
    path: '/checkout/:buyer/:failed?',
  },
  {
    hideReviews: true,
    meta: { title: T.translate('unsubscribePage.metaTitle') },
    path: '/unsubscribe/:sms?',
  },
  {
    path: '/dealers',
  },
  {
    path: '/pro',
  },
  {
    path: '/[vrm]*',
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('acceptOffer.title'),
    meta: {
      description: T.translate('signUpView.metaDescription'),
      title: T.translate('acceptOffer.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/accept-offer`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    noRobots: true,
    path: '/unsupported-browser',
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('confirmSaleSummary.title'),
    meta: {
      description: T.translate('signUpView.metaDescription'),
      title: T.translate('confirmSaleSummary.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/confirm-sale-summary`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('reviewStatus.title'),
    meta: {
      description: T.translate('signUpView.metaDescription'),
      title: T.translate('reviewStatus.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/review-status`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('dealerDetails.title'),
    meta: {
      description: T.translate('signUpView.metaDescription'),
      title: T.translate('dealerDetails.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/purchasing-dealer-details`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('saleCompleteRoute.title'),
    meta: {
      description: T.translate('signUpView.metaDescription'),
      title: T.translate('saleCompleteRoute.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/sale-complete`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('financeFundsReceivedRoute.title'),
    meta: {
      description: T.translate('financeFundsReceivedRoute.metaDescription'),
      title: T.translate('financeFundsReceivedRoute.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/finance-funds-received`,
    vrmPath: true,
  },
  {
    hideFooter: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('saleCompleteRoute.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('saleCompleteRoute.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/confirm-sale`,
    vrmPath: true,
    zendeskWidget: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('addBankDetails.title'),
    meta: {
      description: T.translate('signUpView.metaDescription'),
      title: T.translate('addBankDetails.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/add-bank-details`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('uploadDocuments.title'),
    meta: {
      description: T.translate('signUpView.metaDescription'),
      title: T.translate('uploadDocuments.metaTitle'),
    },
    noRobots: true,
    path: `${routing.vrmPath}/upload-documents/:kind?`,
    vrmPath: true,
  },
  {
    hideReviews: true,
    label: T.translate('sitemap.title'),
    meta: {
      description: T.translate('sitemap.metaDescription'),
      title: T.translate('sitemap.metaTitle'),
    },
    path: SITEMAP_HTML_URL,
  },
  {
    hideReviews: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    noRobots: true,
    path: AFFILIATE_PAGE_ROUTES[0],
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: `${routing.vrmPath}/request/bank-details`,
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: `${routing.vrmPath}/request/bank-details/confirmed`,
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: `${routing.vrmPath}/get-ahead/upload-documents/:kind?`,
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideFooter: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: POST_SALE_DOCS_COLLECTION,
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideFooter: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: EARLY_DOCS_COLLECTION,
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideFooter: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: EARLY_DOCS_VERIFICATION,
    showDisclaimer: true,
    showDisclaimerDetail: true,
  },
  {
    hideFooter: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('homeView.title'),
    meta: {
      description: T.translate('homeView.metaDescription'),
      title: T.translate('homeView.metaTitle'),
    },
    path: `${routing.vrmPath}/vehicle-details`,
    showDisclaimer: false,
    showDisclaimerDetail: false,
  },
  {
    cache: true,
    hideReviews: true,
    label: 'Locations',
    meta: {
      description: 'Locations',
      title: 'Locations',
    },
    path: '/locations',
  },
  {
    hideReviews: true,
    hideVehicleLookup: true,
    label: 'Handshake',
    meta: {
      description: 'Handshake',
      title: 'Handshake',
    },
    path: HANDSHAKE_ROUTE,
  },
  {
    hideFooter: true,
    hideReviews: true,
    hideVehicleLookup: true,
    label: 'Verify Your Identity',
    meta: {
      description: 'Identity verification for sale authorization',
      title: 'Verify Your Identity - Motorway',
    },
    noRobots: true,
    noSitemap: true,
    path: VERIFY_YOUR_IDENTITY_ROUTE,
  },
  {
    cache: true,
    featureFlag: 'showServiceFee',
    hideReviews: true,
    label: T.translate('serviceFeesView.label'),
    meta: {
      description: T.translate('serviceFeesView.metaDescription'),
      title: T.translate('serviceFeesView.metaTitle'),
    },
    path: SERVICE_FEES_ROUTE,
    showDisclaimer: true,
  },
  {
    cache: false,
    hideReviews: true,
    label: 'Car Prize Draw',
    meta: {
      description: 'Car Prize Draw',
      title: 'Car Prize Draw',
    },
    path: [CAR_PRIZE_DRAW_ROUTE, `${CAR_PRIZE_DRAW_ROUTE}/mileage`],
    refreshMetaManually: true,
  },
  {
    cache: false,
    hideReviews: true,
    label: 'Driving Gloves Prize Draw',
    meta: {
      description: 'Driving Gloves Prize Draw',
      title: 'Driving Gloves Prize Draw',
    },
    path: [DRIVING_GLOVES_PRIZE_DRAW_ROUTE, `${DRIVING_GLOVES_PRIZE_DRAW_ROUTE}/mileage`],
  },
]
  .concat(FULL_LANDING_PAGE_ROUTES.map((r) => ({
    cache: true,
    hideReviews: true,
    label: T.translate(`landing${stripStringAndCamelCase(r)}View.title`),
    noRobots: NO_SITEMAP_ROUTES.includes(r),
    noSitemap: NO_SITEMAP_ROUTES.includes(r),
    path: r,
    showDisclaimer: true,
    showDisclaimerDetail: true,
    useUlezVrmChecker: ulezCheckerFooterRoutes.includes(r),
    wordpress: true,
  })))
  .concat(WORDPRESS_ROUTES_CONFIG.map((config) => ({
    cache: true,
    hideReviews: true,
    label: T.translate(`${config.section}View.title`),
    path: config.path,
    wordpress: true,
    ...(config?.zendeskWidget && { zendeskWidget: config.zendeskWidget }),
  })))
  .concat(CUSTOMER_HUB_PATHS.map((route) => ({
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('carValueTrackerView.title'),
    meta: {
      description: T.translate('carValueTrackerView.metaDescription'),
      title: T.translate('carValueTrackerView.metaTitle'),
    },
    noRobots: true,
    path: route,
    showDisclaimer: false,
    showDisclaimerDetail: false,
    showTrackerDisclaimer: route === CUSTOMER_HUB_RESULT_ROUTE,
    ...(route === CUSTOMER_HUB_VALUATION_ROUTE && { noRobots: false, wordpress: true }),
  })))
  .concat(MOT_PAGE_ROUTES.map((route) => ({
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('motCheckView.title'),
    meta: {
      description: T.translate('motCheckView.metaDescription'),
      title: T.translate('motCheckView.metaTitle'),
    },
    path: route,
    showDisclaimer: true,
    showDisclaimerDetail: false,
    ...(route === MOT_VALUATION_ROUTE && { wordpress: true }),
  })))
  .concat(TAX_PAGE_ROUTES.map((route) => ({
    hideReviews: true,
    hideVehicleLookup: true,
    label: T.translate('taxCheckView.title'),
    meta: {
      description: T.translate('taxCheckView.metaDescription'),
      title: T.translate('taxCheckView.metaTitle'),
    },
    path: route,
    showDisclaimer: true,
    showDisclaimerDetail: false,
    ...(route === TAX_VALUATION_ROUTE && { wordpress: true }),
  })))
  .concat(SEO_PAGES_ROUTES.map((r) => ({
    hideReviews: true,
    meta: {
      description: T.translate(`seoPage${stripStringAndCamelCase(r)}.description`),
      title: T.translate(`seoPage${stripStringAndCamelCase(r)}.title`),
    },
    path: r,
  })));

export { routes };
